import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import BaseKeyNumberGrid from './BaseKeyNumberGrid';
import { formatNumberWithSpaces } from 'utils/index';
import './nitrogen-key-data.style.scss';

const PRIORITY_ROWS = ['nitrogenCapital', 'coverGain', 'quantity'];

const NitrogenKeyData = ({ suggest, forecastCampaign, cultures, previous }) => {
  const { t } = useTranslation();
  const [displayUnit, setDisplayUnit] = useState('currency');
  const [columns, setColumns] = useState([]);
  const [isExpanded, setIsExpanded] = useState(false);

  const years = suggest?.years;
  const hasPrevious = !!previous?.nitrogen;

  const getCultureNameByIndex = useCallback(
    (yearData, index) => {
      const distribution = yearData?.culturesDistribution;
      if (distribution?.[index]?.culture?.name) {
        return distribution[index].culture.name;
      }
      return cultures?.[index]?.name || `Culture ${index}`;
    },
    [cultures],
  );

  const getValueFromNitrogen = useCallback(
    (nitrogenData, row) => {
      if (!nitrogenData) return 0;

      if (row.computed === 'needToProvide') {
        const getVal = (field) => {
          const entry = nitrogenData[field];
          if (entry == null) return 0;
          if (typeof entry === 'number') return entry;
          return (displayUnit === 'unit' ? entry.unit : entry.currency) ?? 0;
        };
        return getVal('quantity') - getVal('cropGain') - getVal('coverGain');
      }

      if (row.gainCategory) {
        const list = nitrogenData.gainPerCropPerHectareList?.[row.gainCategory];
        const values = displayUnit === 'unit' ? list?.unit : list?.currency;
        if (!values?.length) return 0;
        return values.reduce((sum, val) => sum + (val || 0), 0);
      }

      if (row.gainCategory2 != null && row.cultureIndex != null) {
        const list = nitrogenData.gainPerCropPerHectareList?.[row.gainCategory2];
        const values = displayUnit === 'unit' ? list?.unit : list?.currency;
        return values?.[row.cultureIndex] ?? 0;
      }

      const entry = nitrogenData[row.id];
      if (entry == null) return 0;
      if (typeof entry === 'number') return entry;
      return (displayUnit === 'unit' ? entry.unit : entry.currency) ?? 0;
    },
    [displayUnit],
  );

  const unitLabel = displayUnit === 'unit' ? 'u' : '€';

  useEffect(() => {
    const renderValue = (params) => (
      <span>
        {formatNumberWithSpaces(params?.value?.toFixed(0) ?? 0)}{' '}
        {displayUnit === 'currency' ? '€' : ''}
      </span>
    );

    const cols = [
      {
        field: 'indicator',
        headerName: `${t('simulation.result.nitrogen.indicator')} (${unitLabel})`,
        flex: 2,
        minWidth: 280,
        sortable: false,
        renderCell: (params) => (
          <span style={{ paddingLeft: params.row.isChild ? '1.5rem' : 0 }}>{params.value}</span>
        ),
      },
    ];

    if (hasPrevious) {
      const prevYear = forecastCampaign?.startYear - 1;
      cols.push({
        field: 'previous',
        headerName: `${prevYear}-${prevYear + 1}`,
        flex: 1,
        minWidth: 120,
        sortable: false,
        headerAlign: 'center',
        align: 'center',
        cellClassName: 'previous-campain',
        renderCell: renderValue,
      });
    }

    years.forEach((_, index) => {
      const startYear = forecastCampaign?.startYear + index;
      cols.push({
        field: `year_${index}`,
        headerName: `${startYear}-${startYear + 1}`,
        flex: 1,
        minWidth: 120,
        sortable: false,
        headerAlign: 'center',
        align: 'center',
        renderCell: renderValue,
      });
    });

    setColumns(cols);
  }, [unitLabel, years, forecastCampaign, hasPrevious, displayUnit, t]);

  const buildCategoryChildren = useCallback(
    (category) => {
      const children = [];
      let maxLength = 0;

      const allNitrogenSources = [
        ...years.map((y) => y?.nitrogen),
        ...(previous?.nitrogen ? [previous.nitrogen] : []),
      ];

      allNitrogenSources.forEach((nitrogen) => {
        const list = nitrogen?.gainPerCropPerHectareList?.[category];
        const unitLen = list?.unit?.length || 0;
        const currLen = list?.currency?.length || 0;
        maxLength = Math.max(maxLength, unitLen, currLen);
      });

      for (let i = 0; i < maxLength; i++) {
        const hasValue = allNitrogenSources.some((nitrogen) => {
          const list = nitrogen?.gainPerCropPerHectareList?.[category];
          return (list?.unit?.[i] || 0) !== 0 || (list?.currency?.[i] || 0) !== 0;
        });

        if (!hasValue) continue;

        const cultureName = getCultureNameByIndex(cultures, i);
        children.push({
          id: `${category}_culture_${i}`,
          indicator: cultureName,
          gainCategory2: category,
          cultureIndex: i,
        });
      }

      return children;
    },
    [years, previous, cultures, getCultureNameByIndex],
  );

  const allRows = useMemo(() => {
    const baseRows = [
      {
        id: 'nitrogenCapital',
        indicator: t('simulation.result.nitrogen.nitrogenCapital'),
        priority: 1,
      },
      {
        id: 'coverGain',
        indicator: t('simulation.result.nitrogen.coverGain'),
        priority: 2,
      },
      { id: 'quantity', indicator: t('simulation.result.nitrogen.totalNeed'), priority: 3 },
      { id: 'cropGain', indicator: t('simulation.result.nitrogen.cropGain') },
      {
        id: 'needToProvide',
        indicator: t('simulation.result.nitrogen.needToProvide'),
        computed: 'needToProvide',
      },
    ];

    const nitrogenFixerChildren = buildCategoryChildren('nitrogenFixer');
    baseRows.push({
      id: 'nitrogenFixerSum',
      indicator: t('simulation.result.nitrogen.nitrogenFixerGain'),
      gainCategory: 'nitrogenFixer',
    });
    nitrogenFixerChildren.forEach((child) => {
      baseRows.push(child);
    });

    const othersChildren = buildCategoryChildren('others');
    baseRows.push({
      id: 'othersSum',
      indicator: t('simulation.result.nitrogen.othersGain'),
      gainCategory: 'others',
    });
    othersChildren.forEach((child) => {
      baseRows.push(child);
    });

    return baseRows.map((row) => {
      let hierarchy;
      if (row.gainCategory) {
        hierarchy = ['cropGain', row.id];
      } else if (row.gainCategory2 === 'nitrogenFixer') {
        hierarchy = ['cropGain', 'nitrogenFixerSum', row.id];
      } else if (row.gainCategory2 === 'others') {
        hierarchy = ['cropGain', 'othersSum', row.id];
      } else {
        hierarchy = [row.id];
      }

      const rowData = {
        ...row,
        hierarchy,
        isChild: hierarchy.length > 1,
      };

      if (hasPrevious) {
        rowData.previous = getValueFromNitrogen(previous.nitrogen, row);
      }

      years.forEach((_, index) => {
        rowData[`year_${index}`] = getValueFromNitrogen(years[index]?.nitrogen, row);
      });

      return rowData;
    });
  }, [displayUnit, years, hasPrevious, previous, getValueFromNitrogen, buildCategoryChildren, t]);

  const displayedRows = useMemo(() => {
    if (isExpanded) return allRows;
    return allRows
      .filter((row) => PRIORITY_ROWS.includes(row.id))
      .sort((a, b) => a.priority - b.priority);
  }, [allRows, isExpanded]);

  const hasMoreRows = allRows.length > PRIORITY_ROWS.length;

  if (!years?.length || !years.some((y) => y?.nitrogen)) {
    return null;
  }

  return (
    <section className="section">
      <div className="section-header">
        <h2 className="section-title">{t('simulation.result.nitrogen.title')}</h2>
        <div className="unit-toggle">
          <button
            className={`unit-btn ${displayUnit === 'currency' ? 'active' : ''}`}
            onClick={() => setDisplayUnit('currency')}
          >
            {t('simulation.result.nitrogen.euros')}
          </button>
          <button
            className={`unit-btn ${displayUnit === 'unit' ? 'active' : ''}`}
            onClick={() => setDisplayUnit('unit')}
          >
            {t('simulation.result.nitrogen.units')}
          </button>
        </div>
      </div>

      <div>
        <BaseKeyNumberGrid rows={displayedRows} columns={columns} treeData={isExpanded} />

        {hasMoreRows && (
          <button className="expand-btn" onClick={() => setIsExpanded(!isExpanded)}>
            {isExpanded ? (
              <>
                <ExpandLessIcon sx={{ fontSize: 18 }} />
                {t('simulation.result.nitrogen.seeLess')}
              </>
            ) : (
              <>
                <ExpandMoreIcon sx={{ fontSize: 18 }} />
                {t('simulation.result.nitrogen.seeMore')}
              </>
            )}
          </button>
        )}
      </div>
    </section>
  );
};

export default NitrogenKeyData;
